import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminAuth } from '@/context/AdminAuthContext'
import { Icon } from './icons'
import { Button } from './ui'

/**
 * 后台管理自己的外壳（`/admin`）。
 *
 * ### 为什么后台**不套工作台外壳**（2026-10-07 用户口径）
 *
 * 用户原话：「http://127.0.0.1:5273/admin，我希望是新页面的，不是嵌入之前的页面中」。
 *
 * 在此之前 `/admin` 是 `RequireAuth` 里的一个子路由 ⇒ 渲染在 `AppShell` 的 `Outlet`
 * 里，于是「后台管理」看起来只是工作台里换了一块内容：左边还是图片/视频那套侧栏、
 * 顶栏还是模板中心/我的作品。更别扭的是**登录那一跳**：未登录时给的是独立的管理员
 * 登录页（整屏、没有外壳），一登进去却换成「带外壳的后台」—— 同一件事前后两副样子。
 *
 * 现在 `/admin` 有自己的路由（`App.AdminRoute`）、自己的外壳：
 * 页头 = 品牌 + 「后台管理」+ 当前账号 + **返回工作台** + 退出登录，正文整幅交给
 * `pages/Admin.tsx`。侧栏与顶栏都不出现 —— 后台是**另一张页面**，不是一个标签。
 *
 * ### 三条口径
 *
 * 1. **不留业务上下文**：这里不挂 `JobCenterContext` 的任务条、不挂素材库抽屉 ——
 *    后台只管上游通道与账号额度。要动业务，走「返回工作台」。
 * 2. **账号必须一眼可见**：后台里改的就是「谁的额度」，页头得写明当前是谁。
 *    ⚠️ 这里显示的是**后台账号**（`useAdminAuth`），不是会员账号 —— 拆表后
 *    两者是两个身份，页头写会员名会让人对着一个跟当前权限无关的名字干活。
 * 3. **品牌标只有一份**（`public/brand-mark.svg`）—— 与侧栏、favicon 同一枚文件，
 *    这里**只给尺寸**，不重画形状。换 logo 时三处一起变。
 *
 * ⚠️ 「退出登录」= **只退出后台**（`AdminAuthContext.logout` 只吊销后台 token）。
 * 页头上不写「工作台不受影响」这种解释：两套登录态是系统设计，不是这次的例外。
 */
export function AdminShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { admin, logout } = useAdminAuth()

  return (
    <div className="adminshell">
      <header className="adminshell__bar">
        <img className="adminshell__mark" src="/brand-mark.svg" alt="" width={28} height={28} />
        <div className="adminshell__head">
          <div className="adminshell__title">后台管理</div>
          <div className="adminshell__sub">上游通道 · 账号与额度</div>
        </div>

        <div className="adminshell__spacer" />

        <span className="adminshell__who">
          <Icon name="shield" size={13} />
          {/* 走到这里必然是后台会话已建立（`AdminRoute` 挡在前面），所以不写
              「未登录」兜底 —— 那是个渲染不出来的分支，留着只会让人以为有第四种状态。 */}
          {admin?.displayName || admin?.account}
        </span>

        {/* **返回工作台**：后台是独立页面，没有侧栏可点，出口必须在页头上写着。
            用 `navigate(-1)` 会让「直接敲地址进来」的人退到站外，所以固定回首页。 */}
        <Button variant="outline" size="sm" icon="panel" onClick={() => navigate('/')}>
          返回工作台
        </Button>
        <Button variant="ghost" size="sm" icon="x" onClick={() => void logout()}>
          退出登录
        </Button>
      </header>

      <div className="adminshell__body">{children}</div>
    </div>
  )
}
